import { sendGTMEvent } from "@next/third-parties/google";

/* ------------------------- LEAD / ENQUIRY EVENTS ------------------------- */

// 🆕 Meta equivalent: Lead
export const submitContactFormEvent = ({
  formName,
  formLocation,
}: {
  formName: string;
  formLocation: string;
}) => {
  sendGTMEvent({
    event: "lead",
    form_name: formName,
    form_location: formLocation,
  });
};

// Old name maintained for backward compatibility
export const enquiryButtonClickEvent = ({
  buttonName,
  pageLocation,
}: {
  buttonName: string;
  pageLocation: string;
}) => {
  sendGTMEvent({
    event: "enquiry_button_click",
    button_name: buttonName,
    page_location: pageLocation,
  });
};

/* --------------------------- SOCIAL / CTA CLICKS -------------------------- */

export const whatsappClickEvent = ({
  buttonName,
  pageLocation,
}: {
  buttonName: string;
  pageLocation: string;
}) => {
  sendGTMEvent({
    event: "whatsapp_click",
    button_name: buttonName,
    page_location: pageLocation,
  });
};

export const callClickEvent = ({
  buttonName,
  pageLocation,
}: {
  buttonName: string;
  pageLocation: string;
}) => {
  sendGTMEvent({
    event: "call_button_click",
    button_name: buttonName,
    page_location: pageLocation,
  });
};

export const emailClickEvent = ({
  buttonName,
  pageLocation,
}: {
  buttonName: string;
  pageLocation: string;
}) => {
  sendGTMEvent({
    event: "email_button_click",
    button_name: buttonName,
    page_location: pageLocation,
  });
};

/* ------------------------------- SEARCH FLOW ------------------------------ */

export const searchEvent = ({
  searchTerm,
  resultsCount,
  pageLocation,
}: {
  searchTerm: string;
  resultsCount: number;
  pageLocation: string;
}) => {
  sendGTMEvent({
    event: "search",
    search_term: searchTerm,
    results_count: resultsCount,
    page_location: pageLocation,
  });
};

export const searchSubmitEvent = ({
  searchTerm,
  pageLocation,
}: {
  searchTerm: string;
  pageLocation: string;
}) => {
  sendGTMEvent({
    event: "search_submit",
    search_term: searchTerm,
    page_location: pageLocation,
  });
};

/* ------------------------- PRODUCT / CATEGORY VIEWS ----------------------- */

// 🆕 Meta equivalent: ViewContent
export const viewProductEvent = ({
  productName,
  productId,
  price,
  currency,
  pageLocation,
}: {
  productName: string;
  productId?: string;
  price?: number;
  currency?: string;
  pageLocation: string;
}) => {
  sendGTMEvent({
    event: "view_content",
    product_name: productName,
    product_id: productId,
    value: price,
    currency,
    page_location: pageLocation,
  });
};

export const collectionViewEvent = ({
  collectionName,
  pageLocation,
}: {
  collectionName: string;
  pageLocation: string;
}) => {
  sendGTMEvent({
    event: "collection_view",
    collection_name: collectionName,
    page_location: pageLocation,
  });
};

export const categoryProductViewEvent = ({
  categoryName,
  pageLocation,
}: {
  categoryName: string;
  pageLocation: string;
}) => {
  sendGTMEvent({
    event: "category_product_view",
    category_name: categoryName,
    page_location: pageLocation,
  });
};

/* ---------------------------- ADD TO CART FLOW ---------------------------- */

export const addToCartEvent = ({
  productId,
  productName,
  productSlug,
  price,
  currency,
  pageLocation,
}: {
  productId: string;
  productSlug: string;
  productName: string;
  price: number;
  currency: string;
  pageLocation: string;
}) => {
  // console.log(productId, productName, productSlug, price);
  sendGTMEvent({
    event: "add_to_cart",
    product_id: productId,
    product_name: productName,
    product_slug: productSlug,
    value: price,
    currency,
    page_location: pageLocation,
  });
};

// ✅ Track Add to Wishlist
export const trackAddToWishlist = ({
  id,
  name,
  price,
  currency = "INR",
}: {
  id: string | number;
  name: string;
  price?: number;
  currency?: string;
}) => {
  // 🔹 Facebook Pixel
  if (typeof window !== "undefined" && window.fbq) {
    window.fbq("track", "AddToWishlist", {
      content_ids: [id],
      content_name: name,
      value: price || 0,
      currency,
      content_type: "product",
    });
  }

  // 🔹 Google Analytics 4
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", "add_to_wishlist", {
      items: [
        {
          item_id: id,
          item_name: name,
          price: price || 0,
          currency,
        },
      ],
      value: price || 0,
      currency,
    });
  }
};

// ✅ Track Remove from Wishlist
export const trackRemoveFromWishlist = ({
  id,
  name,
}: {
  id: string | number;
  name: string;
}) => {
  // 🔹 Facebook Pixel
  if (typeof window !== "undefined" && window.fbq) {
    window.fbq("trackCustom", "RemoveFromWishlist", {
      content_ids: [id],
      content_name: name,
      content_type: "product",
    });
  }

  // 🔹 Google Analytics 4
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", "remove_from_wishlist", {
      items: [{ item_id: id, item_name: name }],
    });
  }
};

export const cartClickEvent = ({ pageLocation }: { pageLocation: string }) => {
  sendGTMEvent({
    event: "cart_button_click",
    page_location: pageLocation,
  });
};

/* ----------------------------- CHECKOUT FLOW ------------------------------ */

// 🆕 Meta equivalent: InitiateCheckout
export const proceedToCheckoutEvent = ({
  pageLocation,
  cartValue,
  numItems,
  currency,
}: {
  pageLocation: string;
  cartValue: number;
  numItems?: number;
  currency?: string;
}) => {
  sendGTMEvent({
    event: "initiate_checkout",
    page_location: pageLocation,
    cart_value: cartValue,
    num_items: numItems,
    currency,
  });
};

// 🆕 Meta equivalent: AddPaymentInfo
export const addPaymentInfoEvent = ({
  paymentMethod,
  pageLocation,
}: {
  paymentMethod: string;
  pageLocation: string;
}) => {
  sendGTMEvent({
    event: "add_payment_info",
    payment_method: paymentMethod,
    page_location: pageLocation,
  });
};

// 🆕 Meta equivalent: Purchase
export const purchaseEvent = ({
  orderId,
  amount,
  currency,
  pageLocation,
}: {
  orderId: string;
  amount: number;
  currency: string;
  pageLocation: string;
}) => {
  sendGTMEvent({
    event: "purchase_success",
    order_id: orderId,
    value: amount,
    currency,
    page_location: pageLocation,
  });
};

/* ------------------------------ PAYMENT STATUS ---------------------------- */

export const paymentInitiatedEvent = ({
  pageLocation,
  amount,
  currency,
  paymentMethod,
}: {
  pageLocation: string;
  amount: number;
  currency: string;
  paymentMethod: string;
}) => {
  sendGTMEvent({
    event: "payment_initiated",
    page_location: pageLocation,
    payment_amount: amount,
    payment_currency: currency,
    payment_method: paymentMethod,
  });
};

export const paymentFailedEvent = ({
  reason,
  pageLocation,
}: {
  reason: string;
  pageLocation: string;
}) => {
  sendGTMEvent({
    event: "payment_failed",
    failure_reason: reason,
    page_location: pageLocation,
  });
};

/* ---------------------------- REVIEW & FEEDBACK --------------------------- */

export const addReviewClickEvent = () => {
  sendGTMEvent({
    event: "review_button_click",
  });
};

export const reportBugEvent = ({
  userEmail,
  userName,
  pageLocation,
}: {
  userEmail: string;
  userName: string;
  pageLocation: string;
}) => {
  sendGTMEvent({
    event: "report_bug_submit",
    email: userEmail,
    user_name: userName,
    page_location: pageLocation,
  });
};

/* ------------------------- ADDRESS / BOOKING EVENTS ----------------------- */

export const addressAddedEvent = ({
  pageLocation,
  country,
  city,
  postalCode,
}: {
  pageLocation: string;
  country: string;
  city: string;
  postalCode: string;
}) => {
  sendGTMEvent({
    event: "address_added",
    page_location: pageLocation,
    address_country: country,
    address_city: city,
    address_postal_code: postalCode,
  });
};

// Optional for hotel/service websites
export const bookingSearch = ({
  checkInDate,
  checkOutDate,
  widgetType,
  pageLocation,
}: {
  checkInDate: string;
  checkOutDate: string;
  widgetType: string;
  pageLocation: string;
}) => {
  sendGTMEvent({
    event: "booking_search",
    page_location: pageLocation,
    check_in_date: checkInDate,
    check_out_date: checkOutDate,
    widget_type: widgetType,
  });
};

/* ------------------------ CUSTOM / CALCULATOR EVENTS ---------------------- */

export const calculatePriceEvent = ({
  productSlug,
  selectedOptions,
  pageLocation,
}: {
  productSlug: string;
  selectedOptions: Record<string, any>;
  pageLocation: string;
}) =>
  sendGTMEvent({
    event: "calculate_price",
    product_slug: productSlug,
    selected_options: JSON.stringify(selectedOptions),
    page_location: pageLocation,
  });
